import { useState } from "react";
import type { Guide, StepIcon } from "../install/platform";

/**
 * The step right after sign-in: put Larder on the home screen.
 *
 * It sits where naming a household sits — full screen, before the larder —
 * rather than in a banner over the list, because a banner is chrome and people
 * are trained to dismiss chrome without reading it. This is not chrome; on iOS
 * it is a precondition for the product working at all, since Web Push is
 * delivered only to an installed web app and never to a tab.
 *
 * The sell is concrete and true. "Get the app" is a request; "we can't remind
 * you about the paneer unless it's on your home screen" is a reason. See
 * docs/decisions/0019-install-is-onboarding.md.
 */
export function InstallApp({
  guide,
  onInstall,
  onLater,
  onFinish,
  /**
   * They have declined several times. Repeating the same pitch to someone who
   * has heard it four times reads as not listening, so the copy stops selling
   * and just states the thing they are missing.
   */
  persistent = false,
}: {
  guide: Guide;
  onInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
  onLater: () => void;
  onFinish: () => void;
  persistent?: boolean;
}) {
  const [busy, setBusy] = useState(false);

  const press = async () => {
    if (!guide.automatic) return onFinish();
    setBusy(true);
    try {
      // "accepted" needs no handling here: the `appinstalled` event fires and
      // the hook flips `installed`, which unmounts this screen from above.
      // "unavailable" means the event evaporated — Chromium withdraws it once
      // the app is installed by another route — so treat it as done rather
      // than stranding the user on a button that no longer does anything.
      const outcome = await onInstall();
      if (outcome === "unavailable") onFinish();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="install" aria-label="install Larder" data-testid="install-step">
      <div className="install-body">
        <span className="brandmark">Larder</span>

        <h2 className="install-title">
          {persistent
            ? "Still in the browser?"
            : "One last thing — put it on your home screen."}
        </h2>
        <p className="install-lede">{guide.title}</p>

        <ul className="install-why">
          <Why glyph="🔔">
            <strong>So we can warn you.</strong> Reminders only reach an installed app — a
            browser tab can't receive them.
          </Why>
          <Why glyph="⚡">
            <strong>One tap from your home screen.</strong> No address bar, no tab to
            lose. It opens like any other app.
          </Why>
          <Why glyph="✈️">
            <strong>Works without signal.</strong> Your larder is on the device, so it
            opens in the shop and in the kitchen.
          </Why>
        </ul>

        {guide.steps.length > 0 && (
          <ol className="install-steps" data-testid="install-steps">
            {guide.steps.map((step, i) => (
              <li key={step.strong} className="install-step">
                <span className="install-step-n" aria-hidden="true">
                  {i + 1}
                </span>
                <span className="install-step-text">
                  {step.text} <strong>{step.strong}</strong>
                </span>
                <Glyph name={step.icon} />
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="install-actions">
        <button
          type="button"
          className="primary"
          onClick={press}
          disabled={busy}
          data-testid="install-confirm"
        >
          {busy ? "Opening…" : guide.confirm}
        </button>
        {/* Quiet, but real. A hard block would strand every browser that cannot
            install at all — desktop Firefox, locked-down devices — behind a
            door with the app on the other side. The nudge escalates instead. */}
        <button
          type="button"
          className="signin-link"
          onClick={onLater}
          data-testid="install-later"
        >
          Not now
        </button>
      </div>
    </section>
  );
}

function Why({ glyph, children }: { glyph: string; children: React.ReactNode }) {
  return (
    <li>
      <span className="install-why-glyph" aria-hidden="true">
        {glyph}
      </span>
      <span>{children}</span>
    </li>
  );
}

/**
 * The actual button they are being told to find.
 *
 * Drawn rather than named, because "the Share button" is a phrase and the thing
 * on screen is a shape — a square with an arrow out of the top, which nobody
 * can picture from the words but everybody recognises on sight. Getting these
 * right is most of why platform-specific instructions beat generic ones.
 */
function Glyph({ name }: { name: StepIcon }) {
  const common = {
    className: "install-glyph",
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.9,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };
  if (name === "share")
    return (
      <svg {...common}>
        <title>Share</title>
        <path d="M12 15V3" />
        <path d="m8 7 4-4 4 4" />
        <path d="M5 13v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6" />
      </svg>
    );
  if (name === "menu")
    return (
      <svg {...common}>
        <title>Menu</title>
        <circle cx="12" cy="5" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none" />
        <circle cx="12" cy="19" r="1.4" fill="currentColor" stroke="none" />
      </svg>
    );
  if (name === "phone")
    return (
      <svg {...common}>
        <title>Phone</title>
        <rect x="6" y="2.5" width="12" height="19" rx="2.5" />
        <path d="M11 18.5h2" />
      </svg>
    );
  if (name === "install")
    return (
      <svg {...common}>
        <title>Install</title>
        <path d="M12 3v12" />
        <path d="m8 11 4 4 4-4" />
        <path d="M4 20h16" />
      </svg>
    );
  return (
    <svg {...common}>
      <title>Add</title>
      <rect x="3.5" y="3.5" width="17" height="17" rx="4" />
      <path d="M12 8.5v7M8.5 12h7" />
    </svg>
  );
}
